import { useState } from 'react'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import { useTranslation } from 'react-i18next'
import { useOwnerAuth } from './OwnerAuthContext'
import { updateStaffProfileName } from '../shared/api/users'
import { errorMessage } from '../shared/api/errorMessage'
import { isPersonName } from '../shared/validation'

/**
 * Self-service profile for any signed-in staff member (owner or staff) — edit the display name.
 * Email and role are read-only (email is the identity; role is set by the owner). Not OWNER-only:
 * every staff member can rename themselves. Saves to `/api/auth/profile`, then adopts the refreshed
 * session so the console header updates immediately.
 */
export default function ProfilePage() {
  const { t } = useTranslation()
  const { session, updateSession } = useOwnerAuth()
  const [name, setName] = useState(session?.name ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const nameError = name.trim() !== '' && !isPersonName(name)
  const valid = name.trim() && !nameError

  const save = async () => {
    if (!valid) {
      return
    }
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      updateSession(await updateStaffProfileName(name.trim()))
      setSaved(true)
    } catch (err) {
      setError(errorMessage(err, t('owner.profile.saveError')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: 3, pb: 8 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {t('owner.profile.title')}
      </Typography>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {saved && <Alert severity="success">{t('owner.profile.saved')}</Alert>}
        <TextField
          label={t('common.yourName')}
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            setSaved(false)
          }}
          fullWidth
          error={nameError}
          helperText={nameError ? t('validation.name') : ' '}
          onKeyDown={(e) => e.key === 'Enter' && save()}
        />
        <TextField label={t('common.email')} value={session?.email ?? ''} fullWidth disabled />
        <TextField label={t('owner.profile.role')} value={session?.role ?? ''} fullWidth disabled />
        <Button variant="contained" size="large" disabled={!valid || saving} onClick={save} sx={{ alignSelf: 'flex-start' }}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </Stack>
    </Container>
  )
}
